import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import InputField from '../../../inputField';
import UserAvatar from '../../../userAvatar';
import CustomButton from '../../../customButton';
import { validateField } from '../../../../../utility/validation';
import { useCustomEffect } from '../../../../../utility/hooks';
import { trimString } from '../../../../../utility/helpers';
import { REPLY_FIELD } from './config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: spacing(4),
      '&:last-of-type': {
        marginBottom: 0,
      },
      [breakpoints.up('sm')]: {
        marginBottom: spacing(6),
      },
    },
    reply: {
      marginBottom: spacing(3),
      [breakpoints.up('sm')]: {
        marginBottom: spacing(6),
      },
    },
    replyHistory: {
      marginBottom: 0,
    },
    replyError: {
      marginBottom: spacing(1),
    },
    replyInput: {
      borderRadius: 8,
      '& textarea': {
        height: 144,
      },
      [breakpoints.up('sm')]: {
        '& textarea': {
          height: 174,
        },
      },
    },
    replyInputText: {
      fontSize: 16,
      lineHeight: '20px',
      '&::placeholder': {
        fontSize: 16,
        lineHeight: '20px',
      },
    },
    subject: {
      marginBottom: spacing(2),
      [breakpoints.up('sm')]: {
        marginBottom: spacing(4),
      },
    },
    userAvatar: {
      height: 20,
      width: 20,
      fontSize: 7,
      lineHeight: '18px',
      [breakpoints.up('sm')]: {
        fontSize: 14,
        lineHeight: 1,
        height: 32,
        width: 32,
      },
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 12,
      lineHeight: '14px',
      whiteSpace: 'nowrap',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
    },
    save: {
      padding: spacing(1, 2),
      marginLeft: 'auto',
      '&:disabled': {
        border: `1px solid ${primary.bluish7}`,
      },
      '& span': {
        fontFamily: 'ProximaNova-Bold',
        fontSize: 12,
        lineHeight: '24px',
        letterSpacing: 'normal',
        textTransform: 'capitalize',
      },
      [breakpoints.up('sm')]: {
        padding: spacing(2, 4),
        '& span': {
          fontSize: 16,
          lineHeight: '24px',
        },
      },
    },
  })
);

const OpenEndedAnswer = ({
  translations,
  questionId,
  subject = {},
  initialAnswer = '',
  questionType,
  error = null,
  isSingleReview = false,
  isHistoryView = false,
  isDisabled = false,
  onSetError,
  onChange,
}) => {
  const classes = useStyles();

  const [answer, setAnswer] = useState(initialAnswer);

  useCustomEffect(
    () => {
      setAnswer(initialAnswer);
      onSetError(null, subject);
    },
    [questionId],
    false
  );

  const handleOnChange = async event => {
    event.persist();
    const { value } = event.target;
    const caretStart = event.target.selectionStart;
    const caretEnd = event.target.selectionEnd;
    const currentError = await validateField(REPLY_FIELD, value);

    setAnswer(value);
    onSetError(currentError, subject);
    event.target.setSelectionRange(caretStart, caretEnd);
  };

  const handleSaveReply = useCallback(() => {
    if (
      !error &&
      trimString(initialAnswer || '') !== trimString(answer || '')
    ) {
      onChange(questionId, questionType, answer, subject);
    }
  }, [
    error,
    initialAnswer,
    answer,
    questionId,
    questionType,
    subject,
    onChange,
  ]);

  return (
    <div className={classes.root}>
      {!isSingleReview && (
        <UserAvatar
          className={classes.subject}
          customAvatarClass={classes.userAvatar}
          labelClass={classes.userLabel}
          user={subject}
          caption
        />
      )}
      <div
        className={classNames(classes.reply, {
          [classes.replyError]: !!error,
          [classes.replyHistory]: isHistoryView,
        })}
      >
        <InputField
          className={classes.replyInput}
          customInputClass={classes.replyInputText}
          name={REPLY_FIELD.name}
          placeholder={translations[REPLY_FIELD.name].placeholder}
          value={answer || ''}
          rows={REPLY_FIELD.rows}
          multiline={REPLY_FIELD.isMultiline}
          error={!!error}
          errorMessage={
            translations[REPLY_FIELD.name].validationMessages?.[error]
          }
          onChange={handleOnChange}
          disabled={isDisabled}
          fullWidth
        />
      </div>
      {!isHistoryView && (
        <div className={classes.footer}>
          <CustomButton
            className={classes.save}
            type="addDarkRoundedOutlined"
            showIcon={false}
            disabled={
              !!error ||
              !trimString(answer || '') ||
              trimString(initialAnswer || '') === trimString(answer || '')
            }
            onClick={handleSaveReply}
          >
            {translations.save}
          </CustomButton>
        </div>
      )}
    </div>
  );
};

OpenEndedAnswer.propTypes = {
  translations: PropTypes.shape({}).isRequired,
  subject: PropTypes.shape({}),
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  questionType: PropTypes.string.isRequired,
  initialAnswer: PropTypes.string,
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isHistoryView: PropTypes.bool,
  error: PropTypes.string,
  onSetError: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default OpenEndedAnswer;
